import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { toast, toastError } from '@/components/ui/toast';
import { useTokenPairStore } from '@/features/tokenPairs';
import { useSpeedBackend, type UserTrade } from '@/features/wallet';
import type { SpeedBackend } from '@/lib/api/speed';
import { isApiError } from '@/lib/errors';
import { isLatencyDebugHost } from '@/lib/latencyDebug';
import { queryKeys } from '@/lib/query';

import { useTradingStore } from '../store';
import { isActionableActiveTrade } from '../utils/activeTrades';

import { ACTIVE_STATUSES } from './useActiveUserTrades';

type CloseState = 'closing' | 'error';

// The backend publishes the close over the trade-status websocket the moment it lands,
// which refreshes the active-trades query. So the fast path here is just watching that
// query — this interval is the backstop for a missed push, not the primary mechanism.
// It used to be a 1s fetch loop issuing the exact same request the query already polls.
const CLOSE_BACKSTOP_INTERVAL_MS = 5_000;
const POLL_TIMEOUT_MS = 10 * 60_000;

/**
 * Resolve once `tradeHash` has left the active-trades list, or `POLL_TIMEOUT_MS`
 * elapses — whichever comes first.
 *
 * Resolves off the shared query cache (fed by websocket pushes) rather than its own
 * request loop, so a close no longer doubles the request rate against `/speed/trades`.
 */
export async function waitUntilGone(
    backend: SpeedBackend,
    networkId: number,
    tradeHash: string,
    queryClient: ReturnType<typeof useQueryClient>,
): Promise<void> {
    const key = queryKeys.trades.active(networkId);

    // absent data is NOT proof the trade is gone — only a list we actually have counts.
    const isGone = () => {
        const data = queryClient.getQueryData<UserTrade[]>(key);
        return (
            Array.isArray(data) && !data.some((t) => t.tradeHash === tradeHash)
        );
    };

    if (isGone()) return;

    await new Promise<void>((resolve) => {
        const deadline = Date.now() + POLL_TIMEOUT_MS;
        let settled = false;

        const finish = () => {
            if (settled) return;
            settled = true;
            unsubscribe();
            clearInterval(backstop);
            resolve();
        };

        const unsubscribe = queryClient.getQueryCache().subscribe(() => {
            if (isGone()) finish();
        });

        // covers a dropped push and a socket that never connected; also enforces the
        // timeout. Refetches directly because the query may be disabled or unmounted,
        // in which case invalidating it would never produce a request.
        const backstop = setInterval(() => {
            if (Date.now() >= deadline) {
                finish();
                return;
            }
            // the SAME status set the active query uses: fetching a narrower
            // one and writing it into that query's cache would drop every row
            // the narrower set omits (a resting limit order, since Resting
            // joined the active list) until the next real refetch.
            void backend
                .fetchUserTrades({
                    networkId,
                    status: ACTIVE_STATUSES,
                    limit: 20,
                })
                .then((trades) => {
                    if (settled) return;
                    queryClient.setQueryData(
                        key,
                        trades.filter(isActionableActiveTrade),
                    );
                    if (isGone()) finish();
                })
                .catch(() => {
                    // transient read failure — the next tick retries
                });
        }, CLOSE_BACKSTOP_INTERVAL_MS);
    });
}

/** What a V2 close of a row with nothing live behind it says (plain, no raw error). */
export const NO_LIVE_POSITION_COPY = 'No live position to close.';

/**
 * V2 refusals that mean "this row has no live position on the wallet unlocked now": the backend's own `noPosition`
 * (its fresh account read found none on that coin) and `walletChanged` (the row's backend belongs to a wallet this
 * browser no longer trades as, see `forgetV2Backend`).
 */
function isDeadRowRefusal(error: unknown): boolean {
    const code = isApiError(error) ? error.body?.code : undefined;
    return code === 'noPosition' || code === 'walletChanged';
}

/**
 * V2 only: before a close goes out, the row must be one the CURRENT backend lists (the active-trades cache, which a
 * wallet change removes) and its coin must hold a live position on the row's side in that backend's latest account
 * read. The browser closes by coin with the key unlocked now, so a card that outlived a wallet change would
 * otherwise close whatever the new wallet holds on the same market.
 */
async function rowIsLiveOnCurrentWallet(
    api: SpeedBackend,
    queryClient: ReturnType<typeof useQueryClient>,
    networkId: number,
    tradeHash: string,
    coin: string,
): Promise<boolean> {
    const listed = queryClient
        .getQueryData<UserTrade[]>(queryKeys.trades.active(networkId))
        ?.find((trade) => trade.tradeHash === tradeHash);
    if (!listed) return false;
    const { position } = await api.fetchPosition(coin);
    if (!position) return false;
    const szi = Number(position.szi);
    return Number.isFinite(szi) && szi !== 0 && szi > 0 === listed.isLong;
}

/**
 * Closes an arbitrary active trade. The Hyperliquid backend closes by coin
 * (a reduce-only IOC); `tradeHash` is used only to track per-row UI state and
 * to poll the active list until the position goes flat.
 *
 * V2 (browser wallet): the close is sent only for a row the current wallet
 * lists with a live position behind it (`rowIsLiveOnCurrentWallet`); a row
 * without one, or a `noPosition` answer, says "No live position to close." and
 * drops the row (its close state, its store slot, its cached list entry)
 * instead of leaving it pending. V1 is unchanged.
 */
export function useCloseActiveTrade() {
    const [closeStates, setCloseStates] = useState<Map<string, CloseState>>(
        () => new Map(),
    );
    // closeTrade (sensitive) stays on MoonHub.API; the active-trades poll reads
    // from factions.backend's speed facet (both bound by the V1 backend).
    const backendRef = useRef<SpeedBackend | null>(null);
    const backend = useSpeedBackend();
    backendRef.current = backend;

    const queryClient = useQueryClient();
    const networkId = useTokenPairStore((s) => s.networkId);

    /** V2: the row has nothing live behind it on this wallet. Say so plainly and drop it, never leave it pending. */
    const dropDeadRow = useCallback(
        (tradeHash: string) => {
            toast.error(NO_LIVE_POSITION_COPY);
            setCloseStates((prev) => {
                if (!prev.has(tradeHash)) return prev;
                const next = new Map(prev);
                next.delete(tradeHash);
                return next;
            });
            useTradingStore.getState()._closeSlotForTrade(tradeHash);
            const activeKey = queryKeys.trades.active(networkId);
            queryClient.setQueryData<UserTrade[]>(activeKey, (trades) =>
                trades?.filter((trade) => trade.tradeHash !== tradeHash),
            );
            // the next read says what is really open
            void queryClient.invalidateQueries({ queryKey: activeKey });
            void queryClient.invalidateQueries({
                queryKey: queryKeys.trades.exposure(networkId),
            });
            void queryClient.invalidateQueries({
                queryKey: queryKeys.speed.positions(),
            });
        },
        [networkId, queryClient],
    );

    const closeTrade = useCallback(
        async (tradeHash: string, coin: string) => {
            const api = backendRef.current;
            if (!api || !coin) return;

            setCloseStates((prev) => new Map(prev).set(tradeHash, 'closing'));

            try {
                if (
                    api.kind === 'v2' &&
                    !(await rowIsLiveOnCurrentWallet(
                        api,
                        queryClient,
                        networkId,
                        tradeHash,
                        coin,
                    ))
                ) {
                    dropDeadRow(tradeHash);
                    return;
                }
                const closeSubmitTime = performance.now();
                await api.closeTrade({ coin });
                const closeMs = Math.round(performance.now() - closeSubmitTime);

                // close accepted — start history polling and wait for the
                // position to leave the active list.
                useTradingStore.getState().setPendingClosedTradeHash(tradeHash);
                await waitUntilGone(api, networkId, tradeHash, queryClient);

                toast.success('Trade closed successfully', {
                    // dev hosts only, matching the fill toast: the latency figure is an
                    // internal metric, not product copy.
                    ...(isLatencyDebugHost()
                        ? { description: `${closeMs}ms` }
                        : {}),
                });

                setCloseStates((prev) => {
                    const next = new Map(prev);
                    next.delete(tradeHash);
                    return next;
                });

                void queryClient.invalidateQueries({
                    queryKey: queryKeys.trades.active(networkId),
                });
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.trades.history(networkId),
                });
                // the close can leave an untracked remainder behind.
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.trades.exposure(networkId),
                });
                void queryClient.invalidateQueries({
                    queryKey: queryKeys.speed.balance(),
                });
            } catch (error) {
                if (api.kind === 'v2' && isDeadRowRefusal(error)) {
                    dropDeadRow(tradeHash);
                    return;
                }
                toastError(error, { operation: 'Close' });
                setCloseStates((prev) => new Map(prev).set(tradeHash, 'error'));
            }
        },
        [dropDeadRow, networkId, queryClient],
    );

    return { closeTrade, closeStates };
}
